import { ApiObject, IdentifyTraits, RudderAnalytics } from '@rudderstack/analytics-js';
import { useCallback, useMemo } from 'react';

export const useRudderAnalytics = () => {
  const analytics = useMemo(() => {
    try {
      const writeKey = import.meta.env.VITE_RUDDER_WRITE_KEY;
      const analyticsInstance = new RudderAnalytics();
      analyticsInstance.load(writeKey, import.meta.env.VITE_RUDDER_DATAPLANE_URL);
      return analyticsInstance;
    } catch (e) {
      console.error('Failed to initialize RS', e);
      return null;
    }
  }, []);

  const trackWrapper = useCallback(
    <T extends (...args: unknown[]) => unknown>(event: string, fn: T, opts?: ApiObject): T => {
      return ((...args: Parameters<T>): ReturnType<T> => {
        const result = fn(...args) as ReturnType<T>;

        if (result instanceof Promise) {
          return result.then((res) => {
            try {
              analytics?.track(event, opts);
            } catch (e) {
              console.error(`Failed event "${event}" with RS`, e);
            }
            return res;
          }) as ReturnType<T>;
        } else {
          try {
            analytics?.track(event, opts);
          } catch (e) {
            console.error(`Failed event "${event}" with RS`, e);
          }
        }

        return result;
      }) as T;
    },
    [analytics]
  );

  const track = useCallback(
    (event: string, opts?: ApiObject) => {
      try {
        analytics?.track(event, opts);
      } catch (e) {
        console.error(`Failed event "${event}" with RS`, e);
      }
    },
    [analytics]
  );

  const identify = useCallback(
    (userId: string, traits?: IdentifyTraits) => {
      try {
        analytics?.identify(userId, traits);
      } catch (e) {
        console.error(`Failed user "${userId}" with RS`, e);
      }
    },
    [analytics]
  );

  const page = useCallback(
    (category: string) => {
      try {
        analytics?.page(category);
      } catch (e) {
        console.error(`Failed page "${category}" with RS`, e);
      }
    },
    [analytics]
  );

  return { track, trackWrapper, identify, page };
};
